import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { localRoutes } from "../../../config";
import { AuthStatus, BetaStatus, RegistrationStatus } from "../../../types";
import { PageSpinner } from "../../components/Spinner";
import { useIgnoreOnboardingValue } from "../../data";
import { LocalNavigate } from "../../hooks/localNavigation";
import { useUser } from "../../hooks/useUser";
import { isAvailableTimeNow } from "../../utils/availableTime";
import { useIsFirstSearchValue } from "../../data/call";

export const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const { isGhost, userAuthStatus, userCompletedRegistration, isBeta, betaStatus } = useUser();
  const isFirstSearch = useIsFirstSearchValue();
  const showedOnboarding = useIgnoreOnboardingValue();

  const isBetaPages = useMemo(() => {
    if (
      [localRoutes.betaFlow.welcome, localRoutes.betaFlow.finish, localRoutes.betaFlow.success].includes(
        location.pathname
      )
    ) {
      return true;
    }

    if (location.pathname.includes(localRoutes.betaFlow.steps.base)) return true;

    return false;
  }, [location.pathname]);

  if (
    userAuthStatus === AuthStatus.LOADING ||
    userCompletedRegistration === RegistrationStatus.LOADING ||
    betaStatus === BetaStatus.LOADING
  ) {
    return <PageSpinner />;
  }

  if (userAuthStatus === AuthStatus.NOT_AUTHENTICATED) {
    window.location.href = localRoutes.login + window.location.search;
  }

  // NOTE: Uncomment this line to enable the NotAvailableTime page
  if (
    userAuthStatus === AuthStatus.AUTHENTICATED &&
    userCompletedRegistration === RegistrationStatus.FINISH &&
    location.pathname.includes(localRoutes.getChannel)
  ) {
    if (!isAvailableTimeNow() && !isGhost && isFirstSearch) {
      return <LocalNavigate to={localRoutes.notAvailableTime} replace />;
    }
  }

  if (
    // userAuthStatus === AuthStatus.AUTHENTICATED &&
    // userCompletedRegistration === RegistrationStatus.FINISH &&
    location.pathname.includes(localRoutes.notAvailableTime)
  ) {
    // NOTE: Uncomment this line to enable the NotAvailableTime page
    if (isAvailableTimeNow() && !isGhost) {
      return <LocalNavigate to={localRoutes.getChannel} replace />;
    }
  }

  if (
    !location.pathname.includes(localRoutes.accountSetupSpecify) &&
    userCompletedRegistration === RegistrationStatus.NOT_FINISH
  ) {
    return <LocalNavigate to={localRoutes.accountSetupSpecify} replace />;
  }

  if (
    location.pathname.includes(localRoutes.accountSetupSpecify) &&
    userCompletedRegistration === RegistrationStatus.FINISH &&
    !isBeta
  ) {
    if (!showedOnboarding) {
      return <LocalNavigate to={localRoutes.onboardingSpecify} replace />;
    }

    return <LocalNavigate to={localRoutes.getChannel} replace />;
  }

  if (
    isBeta &&
    location.pathname.includes(localRoutes.accountSetupSpecify) &&
    userCompletedRegistration === RegistrationStatus.FINISH
  ) {
    return <LocalNavigate to={localRoutes.betaFlow.welcome} replace />;
  }

  if (isBeta && !isBetaPages && !location.pathname.includes(localRoutes.accountSetupSpecify)) {
    if (betaStatus === BetaStatus.NOT_FINISH) {
      return <LocalNavigate to={localRoutes.betaFlow.steps.base} replace />;
    }

    if (betaStatus === BetaStatus.FINISH) {
      return <LocalNavigate to={localRoutes.betaFlow.success} replace />;
    }

    return <LocalNavigate to={localRoutes.betaFlow.welcome} replace />;
  }

  if (!isBeta && isBetaPages) {
    return <LocalNavigate to={localRoutes.getChannel} replace />;
  }

  if (
    userCompletedRegistration === RegistrationStatus.FINISH &&
    !location.pathname.includes(localRoutes.onboardingSpecify) &&
    !showedOnboarding &&
    !isBeta
  ) {
    return <LocalNavigate to={localRoutes.onboardingSpecify} replace />;
  }

  return children;
};
